import { APP_BASENAME, appUrl } from './appNavigation';
import { getAuthAttribution } from './authAttribution';
import { getPromoteKitRedirectAttributionParams } from './promotekit';

const DEFAULT_AUTH_REDIRECT_PATH = '/dashboard';

const getQueryRedirectPath = () => {
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get('redirect_path');
  } catch {
    return null;
  }
};

export const getCurrentPathForAuthRedirect = (location) => {
  if (!location?.pathname) return DEFAULT_AUTH_REDIRECT_PATH;
  return `${location.pathname}${location.search || ''}${location.hash || ''}`;
};

export const getAuthRedirectPath = (locationState) => {
  const from = locationState?.from;
  let path = typeof from === 'string'
    ? from
    : getQueryRedirectPath() || getCurrentPathForAuthRedirect(from);

  if (APP_BASENAME && (path === APP_BASENAME || path.startsWith(`${APP_BASENAME}/`))) {
    path = path.slice(APP_BASENAME.length) || '/dashboard';
  }

  if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\') || [...path].some((char) => char.charCodeAt(0) <= 32)) {
    return DEFAULT_AUTH_REDIRECT_PATH;
  }

  if (/^\/(?:login|signup)(?:[/?#]|$)/.test(path)) {
    return DEFAULT_AUTH_REDIRECT_PATH;
  }

  // Resume only a Premium selection in this request, never historical analytics
  // attribution: a later free signup must not silently reopen an old checkout.
  const params = new URLSearchParams(window.location.search);
  const selectedPlan = !locationState?.freeAccountOnly && /^(?:pro|premium)_(monthly|annual)$/.exec(params.get('plan_intent') || '');
  if (selectedPlan) {
    const redirect = new URL(path, window.location.origin);
    if (/^\/dashboard(?:\/overview)?\/?$/.test(redirect.pathname) && !redirect.searchParams.has('premium')) {
      redirect.searchParams.set('premium', selectedPlan[1]);
      path = redirect.pathname + redirect.search + redirect.hash;
    }
  }
  return path;
};

const setIfPresent = (params, key, value) => {
  if (value === null || value === undefined || value === '') return;
  params.set(key, String(value));
};

const appendAttributionToRedirectUrl = (url, overrides = {}) => {
  const attribution = getAuthAttribution();
  const promotekit = getPromoteKitRedirectAttributionParams();
  const fromLanding = attribution.from_landing || attribution.auth_source === 'landing';

  if (fromLanding) {
    url.searchParams.set('from', 'landing');
  } else {
    setIfPresent(url.searchParams, 'from', attribution.auth_source);
  }

  setIfPresent(url.searchParams, 'landing_path', attribution.landing_path);
  setIfPresent(url.searchParams, 'landing_locale', attribution.landing_locale);
  setIfPresent(url.searchParams, 'cta_id', attribution.cta_id);
  setIfPresent(url.searchParams, 'cta_location', attribution.cta_location);
  setIfPresent(url.searchParams, 'plan_intent', attribution.plan_intent);
  setIfPresent(url.searchParams, 'billing_interval', attribution.billing_interval);
  setIfPresent(url.searchParams, 'auth_method', overrides.auth_method || attribution.auth_method);
  setIfPresent(url.searchParams, 'auth_target', overrides.auth_target || attribution.auth_target);
  setIfPresent(url.searchParams, 'source_intent', attribution.source_intent);
  setIfPresent(url.searchParams, 'intent', attribution.intent);
  setIfPresent(url.searchParams, 'source', attribution.source);
  setIfPresent(url.searchParams, 'promotekit_referral', promotekit.promotekit_referral);
  setIfPresent(url.searchParams, 'via', promotekit.promotekit_via);
  setIfPresent(url.searchParams, 'ref', promotekit.ref);
  setIfPresent(url.searchParams, 'aff', promotekit.aff);
  setIfPresent(url.searchParams, 'promocode', promotekit.promocode);

  return url;
};

export const getAuthRedirectUrl = (locationState, overrides = {}) => {
  const url = new URL(appUrl(getAuthRedirectPath(locationState)));
  appendAttributionToRedirectUrl(url, overrides);
  if (locationState?.freeAccountOnly) {
    url.searchParams.delete('premium');
    url.searchParams.delete('billing_interval');
    url.searchParams.set('plan_intent', 'free');
  }
  return url.toString();
};
